// src/pages/crm/AddLead.js
import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import API_BASE_URL from "../../config";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import statesWithCities from "../../data/statesWithCities";
import { Modal, Button } from "react-bootstrap";
import Tesseract from "tesseract.js";

const TiAddLead = () => {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Add Lead for Tax Invoice | Webyappy";
  }, []);

  const [form, setForm] = useState({
    fullName: "",
    company: "",
    gstNumber: "",
    designation: "",
    mobile: "",
    altMobile: "",
    email: "",
    altEmail: "",
    gender: "",
    address: "",
    state: "",
    city: "",
    pincode: "",
    landmark: "",
    website: "",
    linkedin: "",
    source: "",
    funnelStage: "",
    dropReason: "",
    assignedTo: "",
    followupDate: "",
    priority: "",
    profilePic: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [newSource, setNewSource] = useState("");
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [extractImage, setExtractImage] = useState(null);
  const [extractedText, setExtractedText] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);

  const funnelStages = [
    "New",
    "Contacted",
    "Follow-Up",
    "Qualified",
    "Proposal",
    "Lost",
    "Won",
  ];
  const priorities = ["High", "Medium", "Low"];

  useEffect(() => {
    fetch(`${API_BASE_URL}/crm/get_lead_sources`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setLeadSources(data));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/get_users_list`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setUsers(data.filter((u) => u.is_active)));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/crm/get_business_categories`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCategories(data));
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "profilePic") {
      const file = files[0];
      if (file) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(file.type)) {
          alert("Only JPG, JPEG, and PNG formats are allowed.");
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          alert("File size should not exceed 2MB.");
          return;
        }
        setForm({ ...form, profilePic: file });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const validateDuplicates = () => {
    const errors = [];
    if (form.mobile && form.altMobile && form.mobile === form.altMobile) {
      errors.push(
        "Mobile number and Alternate Mobile number should be different.",
      );
    }
    if (form.email && form.altEmail && form.email === form.altEmail) {
      errors.push("Email ID and Alternate Email ID should be different.");
    }
    return errors;
  };

  const checkDuplicate = async (field, value) => {
    try {
      const res = await fetch(`${API_BASE_URL}/crm/check_duplicate_lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field, value }),
        credentials: "include",
      });
      const data = await res.json();
      return data.exists;
    } catch (err) {
      console.error("Duplicate check error:", err);
      return false;
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
    return emailRegex.test(email);
  };

  const checkFieldDuplicate = async (field, value) => {
    if (!value) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    // 🛡 Check Format first (for email fields only)
    const isEmailField = field === "email" || field === "altEmail";
    if (isEmailField && !isValidEmail(value)) {
      setErrors((prev) => ({
        ...prev,
        [field]: "Enter a valid email address",
      }));
      return;
    }

    // ✅ Format OK, check duplicate
    const exists = await checkDuplicate(field, value);
    setErrors((prev) => ({
      ...prev,
      [field]: exists ? `${field} already exists` : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const requiredErrors = [];
    const duplicateErrors = validateDuplicates();
    const existsMobile = await checkDuplicate("mobile", form.mobile);
    const existsAltMobile =
      form.altMobile && (await checkDuplicate("altMobile", form.altMobile));
    const existsEmail = await checkDuplicate("email", form.email);
    const existsAltEmail =
      form.altEmail && (await checkDuplicate("altEmail", form.altEmail));

    if (existsMobile) duplicateErrors.push("Mobile number already exists.");
    if (existsAltMobile) duplicateErrors.push("Alt mobile already exists.");
    if (existsEmail) duplicateErrors.push("Email already exists.");
    if (existsAltEmail) duplicateErrors.push("Alt email already exists.");

    // Add required field checks
    if (!form.fullName.trim()) requiredErrors.push("Full Name is required.");
    if (!form.company.trim()) requiredErrors.push("Company Name is required.");
    if (!form.gender.trim()) requiredErrors.push("Gender is required.");
    if (!form.mobile.trim()) requiredErrors.push("Mobile Number is required.");
    if (!form.email.trim()) requiredErrors.push("Email ID is required.");

    if (requiredErrors.length > 0 || duplicateErrors.length > 0) {
      alert([...requiredErrors, ...duplicateErrors].join("\n"));
      return;
    }

    setIsSubmitting(true);

    const normalizedForm = {
      ...form,
      state:
        typeof form.state === "object" && form.state !== null
          ? form.state.value
          : form.state,
      city:
        typeof form.city === "object" && form.city !== null
          ? form.city.value
          : form.city,
    };

    const data = new FormData();

    Object.entries(normalizedForm).forEach(([key, value]) => {
      if (key === "profilePic" && value === null) return;

      if (key === "state" || key === "city") {
        if (typeof value === "object" && value !== null && value.value) {
          data.append(key, value.value);
        } else if (typeof value === "string") {
          data.append(key, value);
        }
      } else {
        data.append(key, value);
      }
    });

    try {
      const res = await fetch(`${API_BASE_URL}/invoicing/add_taxinvoice_lead`, {
        method: "POST",
        body: data,
        credentials: "include",
      });

      const result = await res.json();
      console.log("Lead Add Result:", result);

      if (result.status === "success" && result.insert_id) {
        // 🔁 Call get_lead_details using the returned insert_id
        const detailsRes = await fetch(
          `${API_BASE_URL}/crm/get_lead_details/${result.insert_id}`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        const details = await detailsRes.json();
        if (details.success && details.data) {
          // 🔑 Make Add-Lead flow the *only* active selection
          localStorage.setItem(
            "selected_ti_lead",
            JSON.stringify(details.data),
          ); // store Add-Lead
          localStorage.removeItem("selected_tax_lead"); // clear Search flow key
          navigate("/pages/invoicing/create-ti-items");
        } else {
          alert("Lead added but failed to fetch details.");
        }

        setErrors({});
      } else {
        alert("Failed to add lead.");
      }
    } catch (err) {
      console.error("Server error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExtractText = () => {
    if (!extractImage) {
      alert("Please upload an image first.");
      return;
    }

    setIsExtracting(true);

    Tesseract.recognize(extractImage, "eng", {
      logger: (m) => console.log(m), // optional: shows progress
    })
      .then(({ data: { text } }) => {
        setExtractedText(text);
      })
      .catch((err) => {
        console.error("OCR Error:", err);
        alert("Failed to extract text.");
      })
      .finally(() => {
        setIsExtracting(false);
      });
  };

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3 col-lg-8 mx-auto">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold mb-0 p-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/crm/CRM" className="text-muted">
                  CRM
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">Add Lead</span>
              </li>
            </ul>
          </div>

          <div className="card card-custom shadow-sm mb-6">
            <div className="card-body d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <div className="text-dark font-weight-bold font-size-h5 mb-1">
                  Quick Data Entry
                </div>
                <div className="text-muted small">
                  Upload a business card or enter a LinkedIn URL to auto-fill
                  lead information
                </div>
              </div>
              <div className="d-flex gap-2">
                <Link to="/pages/crm/CRM" className="btn btn-light">
                  <i className="fas fa-arrow-left fa-xs"></i> Back
                </Link>
                <button
                  className="btn btn-primary font-weight-bold ml-2"
                  onClick={() => setShowExtractModal(true)}
                >
                  <i className="fas fa-pen fa-xs mr-1"></i> Extract Data
                </button>
              </div>
            </div>
          </div>

          <div className="card card-custom shadow-sm mb-10 mt-3">
            <div className="card-body">
              <h5 className="font-weight-bold mb-4">Lead Information</h5>
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="row">
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="fullName"
                      placeholder="Enter full name"
                      value={form.fullName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Company Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="company"
                      placeholder="Enter company name"
                      value={form.company}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">GST No.</label>
                    <input
                      type="text"
                      className="form-control"
                      name="gstNumber"
                      placeholder="Enter GST No (Optional)"
                      value={form.gstNumber}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Designation</label>
                    <input
                      type="text"
                      className="form-control"
                      name="designation"
                      placeholder="Optional"
                      value={form.designation}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="mobile"
                      placeholder="Enter mobile number"
                      value={form.mobile}
                      onChange={handleChange}
                      onBlur={() => checkFieldDuplicate("mobile", form.mobile)}
                    />
                    {errors.mobile && (
                      <small className="text-danger">{errors.mobile}</small>
                    )}
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Alt. Mobile Number</label>
                    <input
                      type="text"
                      className="form-control"
                      name="altMobile"
                      placeholder="Enter mobile number"
                      value={form.altMobile}
                      onChange={handleChange}
                      onBlur={() =>
                        checkFieldDuplicate("altMobile", form.altMobile)
                      }
                    />
                    {errors.altMobile && (
                      <small className="text-danger">{errors.altMobile}</small>
                    )}
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Email ID <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      placeholder="Enter email address"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={() => checkFieldDuplicate("email", form.email)}
                    />
                    {errors.email && (
                      <small className="text-danger">{errors.email}</small>
                    )}
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Alt. Email ID</label>
                    <input
                      type="email"
                      className="form-control"
                      name="altEmail"
                      placeholder="Enter email address"
                      value={form.altEmail}
                      onChange={handleChange}
                      onBlur={() =>
                        checkFieldDuplicate("altEmail", form.altEmail)
                      }
                    />
                    {errors.altEmail && (
                      <small className="text-danger">{errors.altEmail}</small>
                    )}
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Upload Profile Image</label>
                    <input
                      type="file"
                      className="form-control"
                      name="profilePic"
                      accept="image/*"
                      onChange={handleChange}
                    />
                    <p
                      className="form-text text-danger"
                      style={{ fontSize: "9px" }}
                    >
                      Allowed formats: jpg, png. Max size: 2MB
                    </p>
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">
                      Gender <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex align-items-center mt-2">
                      {["Male", "Female", "Other"].map((gender) => (
                        <div
                          className="form-check form-check-inline mr-4"
                          key={gender}
                        >
                          <input
                            className="form-check-input"
                            type="radio"
                            name="gender"
                            id={`gender-${gender}`}
                            value={gender}
                            checked={form.gender === gender}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`gender-${gender}`}
                          >
                            {gender}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Business Category</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder="Select Category"
                      options={[
                        ...categories.map((cat) => ({
                          label: cat.name,
                          value: cat.name,
                        })),
                        { label: "+ Add New", value: "add_new" },
                      ]}
                      value={
                        form.businessCategory
                          ? {
                              label: form.businessCategory,
                              value: form.businessCategory,
                            }
                          : null
                      }
                      onChange={(option) => {
                        if (option?.value === "add_new") {
                          setShowCategoryModal(true);
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            businessCategory: option?.value || "",
                          }));
                        }
                      }}
                      isClearable
                      isSearchable
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Address</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address"
                      placeholder="Enter full address"
                      value={form.address}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">State</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder="Select State"
                      options={Object.keys(statesWithCities).map((state) => ({
                        label: state,
                        value: state,
                      }))}
                      value={
                        form.state
                          ? { label: form.state, value: form.state }
                          : null
                      }
                      onChange={(option) =>
                        setForm((prev) => ({
                          ...prev,
                          state: option?.value || "",
                          city: "", // Reset city
                        }))
                      }
                      isClearable
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">City</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder={
                        form.state ? "Select City" : "Select state first"
                      }
                      options={
                        form.state
                          ? statesWithCities[form.state].map((city) => ({
                              label: city,
                              value: city,
                            }))
                          : []
                      }
                      value={
                        form.city
                          ? { label: form.city, value: form.city }
                          : null
                      }
                      onChange={(option) =>
                        setForm((prev) => ({
                          ...prev,
                          city: option?.value || "",
                        }))
                      }
                      isDisabled={!form.state}
                      isClearable
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Pincode</label>
                    <input
                      type="text"
                      className="form-control"
                      name="pincode"
                      placeholder="Enter pincode"
                      value={form.pincode}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Landmark</label>
                    <input
                      type="text"
                      className="form-control"
                      name="landmark"
                      placeholder="Nearby landmark (optional)"
                      value={form.landmark}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Website</label>
                    <input
                      type="text"
                      className="form-control"
                      name="website"
                      placeholder="https://example.com"
                      value={form.website}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">LinkedIn</label>
                    <input
                      type="text"
                      className="form-control"
                      name="linkedin"
                      placeholder="https://linkedin.com/in/username"
                      value={form.linkedin}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Lead Source</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder="Select Lead Source"
                      options={[
                        ...leadSources.map((source) => ({
                          label: source.name,
                          value: source.name,
                        })),
                        { label: "+ Add New", value: "add_new" },
                      ]}
                      value={
                        form.source
                          ? { label: form.source, value: form.source }
                          : null
                      }
                      onChange={(option) => {
                        if (option?.value === "add_new") {
                          setShowSourceModal(true);
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            source: option?.value || "",
                          }));
                        }
                      }}
                      isClearable
                    />
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Assigned To</label>
                    <Select
                      className="basic-single shadow-sm"
                      classNamePrefix="select"
                      placeholder="Select User"
                      options={users.map((user) => ({
                        label: user.name,
                        value: user.id,
                      }))}
                      value={
                        form.assignedTo
                          ? {
                              label:
                                users.find((u) => u.id == form.assignedTo)
                                  ?.name || "",
                              value: form.assignedTo,
                            }
                          : null
                      }
                      onChange={(option) =>
                        setForm((prev) => ({
                          ...prev,
                          assignedTo: option?.value || "",
                        }))
                      }
                      isClearable
                      isSearchable
                    />
                  </div>

                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Funnel Stage</label>
                    <select
                      name="funnelStage"
                      className="form-control shadow-sm"
                      value={form.funnelStage}
                      onChange={handleChange}
                    >
                      <option value="">Select Funnel Stage</option>
                      {funnelStages.map((stage) => (
                        <option key={stage} value={stage}>
                          {stage}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group col-md-4 mb-3">
                    <label className="small">Priority</label>
                    <select
                      name="priority"
                      className="form-control shadow-sm"
                      value={form.priority}
                      onChange={handleChange}
                    >
                      <option value="">Select Priority</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                </div>

                <div className="text-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-success font-weight-bold px-5 w-100"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2"></span>{" "}
                        Saving...
                      </>
                    ) : (
                      <>Save & Create Tax Invoice</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Modal show={showSourceModal} onHide={() => setShowSourceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Lead Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control"
            placeholder="Enter new lead source"
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSourceModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              if (!newSource.trim()) return;
              const res = await fetch(`${API_BASE_URL}/crm/add_lead_source`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: newSource }),
                credentials: "include",
              });
              const result = await res.json();
              if (result.status === "success") {
                setLeadSources([...leadSources, { name: newSource }]);
                setForm((prev) => ({ ...prev, source: newSource }));
                setNewSource("");
                setShowSourceModal(false);
              } else {
                alert("Failed to add lead source.");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Business Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control"
            placeholder="Enter new business category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCategoryModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              if (!newCategory.trim()) return;
              const res = await fetch(
                `${API_BASE_URL}/crm/add_business_category`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ name: newCategory }),
                  credentials: "include",
                },
              );
              const result = await res.json();
              if (result.status === "success") {
                setCategories([...categories, { name: newCategory }]);
                setForm((prev) => ({ ...prev, businessCategory: newCategory }));
                setNewCategory("");
                setShowCategoryModal(false);
              } else {
                alert("Failed to add business category.");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showExtractModal}
        onHide={() => setShowExtractModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Extract Data from Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label className="font-weight-bold">Upload Image</label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                setExtractImage(file);
                setExtractedText("");
              }}
            />
          </div>

          <div className="text-right mb-3">
            <Button
              variant="primary"
              onClick={() => {
                if (!extractImage)
                  return alert("Please upload an image first.");
                setIsExtracting(true);
                Tesseract.recognize(extractImage, "eng", {
                  logger: (m) => console.log(m),
                })
                  .then(({ data: { text } }) => setExtractedText(text))
                  .catch((err) => {
                    console.error(err);
                    alert("Failed to extract text.");
                  })
                  .finally(() => setIsExtracting(false));
              }}
              disabled={!extractImage || isExtracting}
            >
              {isExtracting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  Extracting...
                </>
              ) : (
                <>Extract Text</>
              )}
            </Button>
          </div>

          <div className="form-group">
            <label className="font-weight-bold">Extracted Text</label>
            <textarea
              className="form-control"
              rows="10"
              value={extractedText}
              onChange={(e) => setExtractedText(e.target.value)}
            ></textarea>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowExtractModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TiAddLead;
